import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";

const CSV_EXPORTS = [
  { key: "interest-tickets", label: "Coming-soon city sign-ups", path: "/admin/interest/tickets" },
  { key: "interest-vinyl",   label: "Vinyl reservations",        path: "/admin/interest/vinyl"   },
  { key: "interest-merch",   label: "Merch interest",            path: "/admin/interest/merch"   },
  { key: "interest-newsletter", label: "Newsletter signups",     path: "/admin/interest/newsletter" },
  { key: "buyers-wav",       label: "WAV buyers (release blast)",path: "/admin/buyers/wav"      },
  { key: "buyers-tickets",   label: "Ticket buyers (all cities)",path: "/admin/buyers/tickets"  },
  { key: "vinyl-fulfillment",label: "Vinyl fulfillment (status)",path: "/admin/vinyl-fulfillment"},
  { key: "vinyl-orders",     label: "Vinyl orders to ship",      path: "/admin/vinyl-orders"     },
];

/* External Stripe Payment Links — these are the standalone "Buy →" pages
   created in the Stripe Dashboard. Used for Linktree, IG bio, posters etc.
   Update each URL when you swap from test → live Stripe keys. */
const EXTERNAL_LINKS = [
  { key: "ext-wav",    label: "Metamorph · Pay-what-you-can WAV", url: "https://buy.stripe.com/test_aFa3cvbh5bzg5A8f3K0Jq02" },
  { key: "ext-film",   label: "Myth · Live Performance Film",     url: "https://buy.stripe.com/test_bJedR9fxl7j03s0g7O0Jq00" },
  { key: "ext-sydney", label: "Metamorph Live · Sydney",          url: "https://buy.stripe.com/test_6oU6oH1Gv46Oe6E08Q0Jq01" },
];

/* Quick-checkout deep links — these route through the SAME backend cart
   flow as the website, so PDF tickets, confirmation emails, capacity
   counting and the daily digest all fire automatically. Drop these
   straight into Linktree / IG bio / posters. */
const QUICK_LINKS = [
  { key: "qc-wav-pwyc",   slug: "wav-pwyc",   label: "Metamorph · Pay what you can",   hint: "Visitor picks WAV amount, then Stripe" },
  { key: "qc-sydney",     slug: "sydney",     label: "Sydney Tickets · $100",          hint: "1 × Sydney ticket — direct to Stripe" },
  { key: "qc-adelaide",   slug: "adelaide",   label: "Adelaide Tickets · $100",        hint: "1 × Adelaide ticket — direct to Stripe" },
  { key: "qc-wav-20",     slug: "wav",        label: "Metamorph Pre-order · $20",      hint: "WAV download @ $20 — direct to Stripe" },
  { key: "qc-wav-30",     slug: "wav-30",     label: "Metamorph Pre-order · $30",      hint: "WAV download @ $30 — direct to Stripe" },
  { key: "qc-film-20",    slug: "film",       label: "Myth Performance Film · $20",    hint: "Film download @ $20 — direct to Stripe" },
];

const fmt$ = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function AdminDashboard() {
  // Magic-link admin key — `/admin#k=<key>` auto-signs in.
  const magicKey = (() => {
    if (typeof window === "undefined") return "";
    const m = (window.location.hash || "").match(/[#&]k=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : "";
  })();
  if (magicKey) {
    try { localStorage.setItem("metamorph_admin_key", magicKey); } catch { /* ignore */ }
  }
  useEffect(() => {
    if (!magicKey || typeof window === "undefined") return;
    const cleanHash = (window.location.hash || "").replace(/[#&]k=[^&]+/, "").replace(/^#/, "");
    window.history.replaceState(null, "", window.location.pathname + window.location.search + (cleanHash ? "#" + cleanHash : ""));
  }, [magicKey]);

  const [adminKey, setAdminKey] = useState(() => {
    try { return localStorage.getItem("metamorph_admin_key") || ""; } catch { return ""; }
  });
  const [authed, setAuthed] = useState(() => {
    try { return !!localStorage.getItem("metamorph_admin_key"); } catch { return false; }
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastLoadedRef = useRef(false);

  const load = async (key) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/admin/summary", { headers: { "X-Admin-Key": key } });
      setData(data);
      setAuthed(true);
      try { localStorage.setItem("metamorph_admin_key", key); } catch { /* ignore */ }
    } catch (err) {
      if (err?.response?.status === 401) {
        setError("Incorrect admin key.");
        try { localStorage.removeItem("metamorph_admin_key"); } catch { /* ignore */ }
        setAuthed(false);
      } else { setError("Could not reach the server."); }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!authed) return undefined;
    const t = setTimeout(() => {
      if (lastLoadedRef.current) return;
      lastLoadedRef.current = true;
      load(adminKey);
    }, 0);
    return () => clearTimeout(t);
  }, [authed, adminKey]);

  const onSignIn = (e) => {
    e.preventDefault();
    if (!adminKey.trim()) return;
    load(adminKey.trim());
  };
  const onSignOut = () => {
    try { localStorage.removeItem("metamorph_admin_key"); } catch { /* ignore */ }
    setAdminKey(""); setAuthed(false); setData(null);
    lastLoadedRef.current = false;
  };
  const onDownloadCsv = (path) => {
    window.open(`${API}${path}?fmt=csv&key=${encodeURIComponent(adminKey)}`, "_blank", "noopener,noreferrer");
  };

  if (!authed) {
    return (
      <main className="min-h-screen bg-black text-white flex flex-col px-6 py-10" data-testid="admin-gate">
        <header className="mb-12">
          <Link to="/" className="vintage-link text-xs sm:text-sm uppercase">← Back</Link>
        </header>
        <div className="flex-1 flex items-center justify-center">
          <form onSubmit={onSignIn} className="w-full max-w-sm">
            <h1 className="vintage-text text-4xl sm:text-5xl mb-2 text-center">Admin</h1>
            <p className="vintage-link text-[10px] uppercase tracking-[0.22em] text-center opacity-70 mb-10">
              Orders · emails · interest lists · door check-in
            </p>
            <input type="password" value={adminKey} onChange={(e) => setAdminKey(e.target.value)}
                   placeholder="Admin key" className="line-input" autoFocus data-testid="admin-key-input" />
            <button type="submit" className="btn-line btn-filled w-full justify-center mt-6" data-testid="admin-signin">
              {loading ? "Checking…" : "Enter"}
            </button>
            {error && <p className="text-rose-300/80 text-xs text-center mt-4 font-mono">{error}</p>}
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-black text-white px-4 sm:px-8 py-5 font-mono" data-testid="admin-dashboard">
      <header className="flex items-center justify-between mb-6 max-w-7xl mx-auto pb-3 border-b border-white/15">
        <Link to="/" className="text-xs uppercase tracking-wider opacity-70 hover:opacity-100">← Site</Link>
        <h1 className="text-sm uppercase tracking-[0.3em]">Metamorph · Admin</h1>
        <div className="flex items-center gap-5 text-xs uppercase tracking-wider">
          <button onClick={() => load(adminKey)} disabled={loading} className="opacity-70 hover:opacity-100" data-testid="admin-refresh">
            {loading ? "Loading…" : "Refresh"}
          </button>
          <button onClick={onSignOut} className="opacity-70 hover:opacity-100" data-testid="admin-signout">Sign out</button>
        </div>
      </header>

      {error && (
        <div className="max-w-7xl mx-auto mb-4 border border-rose-500/40 bg-rose-500/10 px-4 py-2 text-rose-200 text-xs">
          {error}
        </div>
      )}

      {!data ? (
        <p className="max-w-7xl mx-auto py-16 text-center opacity-50 text-xs uppercase tracking-wider">
          {loading ? "Loading…" : "—"}
        </p>
      ) : (
        <div className="max-w-7xl mx-auto space-y-7 pb-16" data-testid="admin-content">
          {/* Top-line stats — single row of text */}
          <p className="text-xs leading-loose tracking-wide opacity-90 pb-3 border-b border-white/10">
            Revenue: <b className="text-[var(--mm-accent)]">{fmt$(data.revenue.week)}</b> / 7d ·
            &nbsp;<b className="text-[var(--mm-accent)]">{fmt$(data.revenue.all_time)}</b> all-time &nbsp;|&nbsp;
            Orders: <b className="text-[var(--mm-accent)]">{data.orders.week}</b> / 7d ·
            &nbsp;<b className="text-[var(--mm-accent)]">{data.orders.all_time}</b> all-time &nbsp;|&nbsp;
            Vinyl:&nbsp;
            <b className="text-[var(--mm-accent)]" data-testid="oversight-vinyl-units">
              {data.by_product.vinyl_paid.units || 0}
            </b> sold ·&nbsp;
            <b className="text-[var(--mm-accent)]" data-testid="oversight-vinyl-revenue">
              {fmt$(data.by_product.vinyl_paid.revenue)}
            </b> ·&nbsp;
            <b className="text-[var(--mm-accent)]" data-testid="oversight-vinyl-reserved">
              {data.interest.vinyl_total_qty || 0}
            </b> reserved
          </p>

          <Section title="By product">
            <SpreadsheetTable
              cols={["Product", "Buyers", "Units", "Revenue", "Note"]}
              rows={[
                ["WAV pre-orders", data.by_product.wav.buyers, "—", fmt$(data.by_product.wav.revenue), ""],
                ["Myth Film",      data.by_product.film.buyers, "—", fmt$(data.by_product.film.revenue),
                  data.by_product.film.buyers > 0
                    ? `${data.by_product.film.downloads || 0} downloads · ${data.by_product.film.downloaders || 0} of ${data.by_product.film.buyers} buyers`
                    : ""],
                ["Vinyl (paid)",   data.by_product.vinyl_paid.buyers, data.by_product.vinyl_paid.units || 0,
                  fmt$(data.by_product.vinyl_paid.revenue),
                  `${data.interest.vinyl_total_qty} reserved / ${data.interest.vinyl_threshold} needed`],
                ["Uphill",         data.by_product.uphill.buyers, "—", fmt$(data.by_product.uphill.revenue), ""],
              ]}
            />
          </Section>

          <Section title="Live shows" right={
            <Link to="/admin/scan" className="text-[10px] uppercase tracking-wider opacity-70 hover:opacity-100">→ Door scan</Link>
          }>
            <SpreadsheetTable
              cols={["City","Date","Venue","Paid / Comp / Cap","Revenue","Checked in"]}
              rows={data.cities.map((c) => {
                const paid = c.paid_tickets ?? c.tickets_sold;
                const comp = c.comp_tickets ?? 0;
                const ccy = c.city === "london" ? "£" : c.city === "paris" ? "€" : "$";
                return [
                  c.label,
                  c.date || "—",
                  c.venue || "(TBA)",
                  `${paid} + ${comp} / ${c.capacity}`,
                  `${ccy}${Number(c.revenue || 0).toFixed(2)}`,
                  c.tickets_sold > 0
                    ? `${c.checked_in} (${Math.round((c.checked_in / c.tickets_sold) * 100)}%)`
                    : "—",
                ];
              })}
            />
          </Section>

          <Section title="Interest lists">
            <SpreadsheetTable
              cols={["List","Count","Note"]}
              rows={[
                ["Tickets · Mel/Lon/Par (coming-soon sign-ups)", data.interest.ticket_interest, ""],
                ["Vinyl reservations", data.interest.vinyl_reservations,
                  `${data.interest.vinyl_total_qty} of ${data.interest.vinyl_threshold} units reserved`],
                ["Merch interest", data.interest.merch_interest, ""],
                ["Newsletter signups (direct)", data.interest.newsletter_signups || 0, "Pre-Resend — captured into mailing list"],
              ]}
            />
          </Section>

          <Section title="Exports · CSV">
            <SpreadsheetTable
              cols={["List","Download"]}
              rows={CSV_EXPORTS.map((ex) => [
                ex.label,
                <button key={ex.key} onClick={() => onDownloadCsv(ex.path)} className="underline hover:text-[var(--mm-accent)]" data-testid={`csv-${ex.key}`}>
                  Download CSV ↓
                </button>,
              ])}
            />
          </Section>

          <Section title="Quick-checkout links" right={
            <span className="text-[10px] opacity-50">For Linktree · IG bio · posters · QR codes</span>
          }>
            <p className="text-[11px] opacity-70 mb-3 leading-relaxed">
              One-tap deep links — each opens straight into Stripe Checkout for that single item.
              All purchases flow through the same backend as the store, so PDF tickets, confirmation
              emails, ticket capacity and the daily digest fire automatically. Custom amounts can be
              added by editing the URL — e.g. <code className="opacity-80">/buy/wav-45</code>.
            </p>
            <SpreadsheetTable
              cols={["What it sells","URL","Copy"]}
              rows={QUICK_LINKS.map((l) => {
                const url = (typeof window !== "undefined" ? window.location.origin : "") + "/buy/" + l.slug;
                return [
                  <span key={l.key+"l"}>
                    <span className="block">{l.label}</span>
                    <span className="block text-[10px] opacity-50">{l.hint}</span>
                  </span>,
                  <a key={l.key+"a"} href={url} target="_blank" rel="noreferrer noopener" className="underline opacity-80 hover:opacity-100 truncate inline-block max-w-[420px]" data-testid={`quick-link-${l.slug}`}>{url}</a>,
                  <CopyButton key={l.key+"c"} value={url} testid={`copy-quick-${l.slug}`} />,
                ];
              })}
            />
          </Section>

          <Section title="External Stripe payment links (legacy)" right={
            <span className="text-[10px] opacity-50">Update on go-live</span>
          }>
            <SpreadsheetTable
              cols={["Product","Stripe link","Copy"]}
              rows={EXTERNAL_LINKS.map((l) => [
                l.label,
                <a key={l.key+"a"} href={l.url} target="_blank" rel="noreferrer noopener" className="underline opacity-80 hover:opacity-100 truncate inline-block max-w-[420px]">{l.url}</a>,
                <CopyButton key={l.key+"c"} value={l.url} testid={`copy-${l.key}`} />,
              ])}
            />
          </Section>

          {/* WAV release blast tool */}
          <WavReleaseTool adminKey={adminKey} onSent={() => load(adminKey)} sentCount={data.wav_release_sent_count || 0} />

          {/* Vinyl fulfillment blast + per-reservation links */}
          <VinylFulfillmentTool adminKey={adminKey} />

          {/* Vinyl orders to ship — addresses + tracking */}
          <VinylOrdersToShip adminKey={adminKey} />

          {/* Quick share links + presale logging */}
          <ShareLinksPanel adminKey={adminKey} />

          {/* Newsletter signups grouped by city + interests */}
          <NewsletterSignupsBrowser adminKey={adminKey} />

          {/* Daily digest test-send */}
          <DailyDigestTool adminKey={adminKey} />

          <Section title={`Recent orders · last ${data.recent_orders.length}`}>
            <SpreadsheetTable
              cols={["Date","Name","Email","Items","Total","Email"]}
              rows={data.recent_orders.map((o) => [
                fmtDate(o.paid_at),
                o.name || "—",
                o.email || "—",
                o.items.join(" · ") || "—",
                fmt$(o.amount),
                o.email_sent_at ? "sent" : "—",
              ])}
            />
          </Section>

          <p className="pt-4 border-t border-white/10 text-[11px] opacity-70 flex flex-wrap gap-x-6 gap-y-2 uppercase tracking-wider">
            <Link to="/admin/tickets" className="hover:opacity-100 opacity-80">→ Tickets table</Link>
            <Link to="/admin/scan" className="hover:opacity-100 opacity-80">→ Door scan</Link>
            <a href="https://dashboard.stripe.com/test/payments" target="_blank" rel="noreferrer noopener" className="hover:opacity-100 opacity-80">→ Stripe ↗</a>
            <a href="https://resend.com/emails" target="_blank" rel="noreferrer noopener" className="hover:opacity-100 opacity-80">→ Resend ↗</a>
          </p>
        </div>
      )}
    </main>
  );
}

/* ─── Section + Table primitives ───────────────────────────────────── */

function Section({ title, right = null, children }) {
  return (
    <section>
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-[10px] uppercase tracking-[0.28em] opacity-65">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

function SpreadsheetTable({ cols, rows }) {  return (
    <div className="border border-white/15 overflow-x-auto">
      <table className="w-full text-[12px] tabular-nums">
        <thead className="bg-white/[0.04]">
          <tr>
            {cols.map((c) => (
              <th key={c} className="text-left px-3 py-1.5 border-b border-white/15 font-normal uppercase tracking-wider text-[10px] opacity-65">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={cols.length} className="px-3 py-4 text-center opacity-45 text-[11px]">No data.</td></tr>
          )}
          {rows.map((row, i) => (
            <tr key={i} className="hover:bg-white/[0.025] border-b border-white/5 last:border-b-0">
              {row.map((cell, j) => (
                <td key={j} className="px-3 py-1.5 align-top">{cell ?? "—"}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Small utilities ──────────────────────────────────────────────── */

function CopyButton({ value, testid }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch {/* ignore */}
      document.body.removeChild(ta);
    }
  };
  return (
    <button
      onClick={onClick}
      className={`underline ${copied ? "text-emerald-300" : "hover:text-[var(--mm-accent)]"}`}
      data-testid={testid}
    >
      {copied ? "Copied ✓" : "Copy"}
    </button>
  );
}

/* ─── WAV release blast tool ───────────────────────────────────────── */

function WavReleaseTool({ adminKey, onSent, sentCount }) {
  const [url, setUrl] = useState("");
  const [subject, setSubject] = useState("Metamorph is here — your download is ready");
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmStage, setConfirmStage] = useState(false);

  const loadPreview = async () => {
    try {
      const { data } = await api.get("/admin/wav-release/preview", { headers: { "X-Admin-Key": adminKey } });
      setPreview(data);
    } catch { setPreview({ error: true }); }
  };

  const onSend = async () => {
    if (!url.trim()) return;
    setSending(true);
    setResult(null);
    try {
      const { data } = await api.post(
        "/admin/wav-release/send",
        { download_url: url.trim(), subject: subject.trim() },
        { headers: { "X-Admin-Key": adminKey } },
      );
      setResult(data);
      onSent?.();
    } catch (e) { setResult({ error: e?.response?.data?.detail || "Send failed" }); }
    finally { setSending(false); setConfirmStage(false); }
  };

  return (
    <Section
      title="Send WAV release"
      right={
        <button onClick={loadPreview} className="text-[10px] uppercase tracking-wider opacity-70 hover:opacity-100" data-testid="wav-release-refresh">
          Refresh list
        </button>
      }
    >
      <div className="border border-white/15 px-4 py-4 space-y-3 text-[12px]">
        <p className="opacity-80 leading-relaxed">
          Sends each WAV buyer (de-duped by email) a one-shot release email with the download link below.
          Idempotent — buyers already sent are skipped.
          {sentCount > 0 && <> Already sent to <b className="text-[var(--mm-accent)]">{sentCount}</b> buyers.</>}
        </p>

        <label className="block">
          <span className="opacity-60 uppercase tracking-wider text-[10px]">Download URL · Dropbox or any direct link</span>
          <input
            type="url" value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.dropbox.com/scl/fi/.../metamorph.wav?dl=1"
            className="w-full bg-black border border-white/20 focus:border-[var(--mm-accent)] outline-none px-2 py-1.5 mt-1 text-[12px]"
            data-testid="wav-release-url"
          />
        </label>

        <label className="block">
          <span className="opacity-60 uppercase tracking-wider text-[10px]">Subject line</span>
          <input
            type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
            className="w-full bg-black border border-white/20 focus:border-[var(--mm-accent)] outline-none px-2 py-1.5 mt-1 text-[12px]"
            data-testid="wav-release-subject"
          />
        </label>

        {preview && !preview.error && (
          <div className="border border-white/10 px-3 py-2 bg-white/[0.025]">
            <span className="opacity-80">
              <b>{preview.total_unique_buyers}</b> unique buyers · <b>{preview.already_sent}</b> already received ·{" "}
              <b className="text-[var(--mm-accent)]">{preview.remaining}</b> would be emailed now.
            </span>
          </div>
        )}
        {preview?.error && <p className="text-rose-300/80">Couldn&apos;t load list.</p>}

        <div className="flex flex-wrap items-center gap-3 pt-2">
          {!confirmStage ? (
            <button
              onClick={() => { setConfirmStage(true); loadPreview(); }}
              disabled={!url.trim()}
              className="border border-[var(--mm-accent)]/70 text-[var(--mm-accent)] px-3 py-1.5 uppercase tracking-wider text-[10px] disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="wav-release-prepare"
            >
              Prepare send →
            </button>
          ) : (
            <>
              <button
                onClick={onSend}
                disabled={sending || !url.trim()}
                className="bg-[var(--mm-accent)] text-black px-3 py-1.5 uppercase tracking-wider text-[10px] disabled:opacity-30"
                data-testid="wav-release-send"
              >
                {sending ? "Sending…" : `Send to ${preview?.remaining ?? "all"} buyers now`}
              </button>
              <button
                onClick={() => { setConfirmStage(false); setResult(null); }}
                className="opacity-60 hover:opacity-100 uppercase tracking-wider text-[10px]"
              >
                Cancel
              </button>
            </>
          )}
        </div>

        {result && !result.error && (
          <p className="border-l-2 border-emerald-400/60 pl-3 py-1 text-emerald-200" data-testid="wav-release-result">
            Sent: <b>{result.sent}</b>. Failed: <b>{result.failed_count}</b>.
            {result.failed_count > 0 && (
              <span className="block opacity-70 mt-1">Bounced/failed: {result.failed.join(", ")}</span>
            )}
          </p>
        )}
        {result?.error && (
          <p className="border-l-2 border-rose-400/60 pl-3 py-1 text-rose-200">{result.error}</p>
        )}
      </div>
    </Section>
  );
}


/* ─── Daily digest tool ───────────────────────────────────────────── */
function DailyDigestTool({ adminKey }) {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const onSend = async () => {
    setSending(true);
    setResult(null);
    try {
      const { data } = await api.post(
        "/admin/daily-digest/send",
        {},
        { headers: { "X-Admin-Key": adminKey } },
      );
      setResult({ ok: true, to: data.sent_to });
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || "Send failed" });
    } finally { setSending(false); }
  };

  return (
    <Section title="Daily digest">
      <div className="border border-white/15 px-4 py-4 space-y-3 text-[12px]">
        <p className="opacity-80 leading-relaxed">
          Auto-sent every morning at <b>08:00 Sydney time</b> to{" "}
          <code className="opacity-90">gm@theprocess.studio</code>. Includes the last 24h of paid orders,
          ticket sales, and new vinyl/merch/tour interest entries.
        </p>
        <div className="flex items-center gap-3">
          <button
            onClick={onSend}
            disabled={sending}
            className="border border-[var(--mm-accent)]/70 text-[var(--mm-accent)] px-3 py-1.5 uppercase tracking-wider text-[10px] disabled:opacity-30"
            data-testid="digest-send-now"
          >
            {sending ? "Sending…" : "Send a test digest now →"}
          </button>
          {result?.ok && (
            <span className="text-emerald-200 text-[11px]" data-testid="digest-send-result">
              Sent to {result.to} ✓
            </span>
          )}
          {result?.error && (
            <span className="text-rose-200 text-[11px]">{result.error}</span>
          )}
        </div>
      </div>
    </Section>
  );
}



/* ─── Vinyl fulfillment tool ─────────────────────────────────────── */
function VinylFulfillmentTool({ adminKey }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState("Your Metamorph Vinyl LP — time to confirm");
  const [confirmStage, setConfirmStage] = useState(false);
  const [resend, setResend] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/vinyl-fulfillment", {
        headers: { "X-Admin-Key": adminKey },
      });
      setStatus(data);
    } catch {
      setStatus({ error: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!adminKey) return undefined;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/vinyl-fulfillment", {
          headers: { "X-Admin-Key": adminKey },
        });
        if (alive) setStatus(data);
      } catch {
        if (alive) setStatus({ error: true });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [adminKey]);

  const onBlast = async () => {
    setSending(true);
    setResult(null);
    try {
      const { data } = await api.post(
        "/admin/vinyl-fulfillment/send",
        { subject: subject.trim(), dry_run: false, resend },
        { headers: { "X-Admin-Key": adminKey } },
      );
      setResult(data);
      load();
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || "Send failed" });
    } finally {
      setSending(false);
      setConfirmStage(false);
    }
  };

  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <Section
      title="Vinyl fulfillment"
      right={
        <button
          onClick={load}
          className="text-[10px] uppercase tracking-wider opacity-70 hover:opacity-100"
          data-testid="vinyl-fulfill-refresh"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      }
    >
      <div className="border border-white/15 px-4 py-4 space-y-3 text-[12px]">
        <p className="opacity-80 leading-relaxed">
          Sends every outstanding reservation holder a personalised Stripe payment link.
          Each link lets the buyer adjust quantity (1–5) — <b className="text-[var(--mm-accent)]">20% off auto-applies on 2+ records</b>.
          Shipping is calculated by Stripe based on destination.
        </p>

        {status && !status.error && (
          <div className="border border-white/10 px-3 py-2 bg-white/[0.025] text-[12px]">
            <span className="opacity-80">
              Total reservations: <b>{status.counts.total}</b> ·
              Outstanding: <b className="text-[var(--mm-accent)]">{status.counts.outstanding}</b> ·
              Sent: <b>{status.counts.sent}</b> ·
              Paid: <b className="text-emerald-300">{status.counts.paid}</b>
            </span>
          </div>
        )}

        <label className="block">
          <span className="opacity-60 uppercase tracking-wider text-[10px]">Email subject line</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full bg-black border border-white/20 focus:border-[var(--mm-accent)] outline-none px-2 py-1.5 mt-1 text-[12px]"
            data-testid="vinyl-fulfill-subject"
          />
        </label>

        <label className="flex items-center gap-2 text-[11px] opacity-80">
          <input
            type="checkbox"
            checked={resend}
            onChange={(e) => setResend(e.target.checked)}
            data-testid="vinyl-fulfill-resend-checkbox"
          />
          <span>Re-send to reservations that already received an invite</span>
        </label>

        <div className="flex flex-wrap items-center gap-3 pt-1">
          {!confirmStage ? (
            <button
              onClick={() => setConfirmStage(true)}
              disabled={!status || status.error || (status.counts.outstanding === 0 && !resend)}
              className="border border-[var(--mm-accent)]/70 text-[var(--mm-accent)] px-3 py-1.5 uppercase tracking-wider text-[10px] disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="vinyl-fulfill-prepare"
            >
              Prepare blast →
            </button>
          ) : (
            <>
              <button
                onClick={onBlast}
                disabled={sending}
                className="bg-[var(--mm-accent)] text-black px-3 py-1.5 uppercase tracking-wider text-[10px] disabled:opacity-30"
                data-testid="vinyl-fulfill-send"
              >
                {sending
                  ? "Sending…"
                  : `Send to ${resend ? status?.counts.total : status?.counts.outstanding} holder${(resend ? status?.counts.total : status?.counts.outstanding) === 1 ? "" : "s"} now`}
              </button>
              <button
                onClick={() => { setConfirmStage(false); setResult(null); }}
                className="opacity-60 hover:opacity-100 uppercase tracking-wider text-[10px]"
              >
                Cancel
              </button>
            </>
          )}
        </div>

        {result && !result.error && (
          <p
            className="border-l-2 border-emerald-400/60 pl-3 py-1 text-emerald-200"
            data-testid="vinyl-fulfill-result"
          >
            Sent: <b>{result.sent}</b>. Failed: <b>{result.failed?.length || 0}</b>.
            {(result.failed?.length || 0) > 0 && (
              <span className="block opacity-70 mt-1">Bounced: {result.failed.join(", ")}</span>
            )}
          </p>
        )}
        {result?.error && (
          <p className="border-l-2 border-rose-400/60 pl-3 py-1 text-rose-200">{result.error}</p>
        )}

        {status?.rows?.length > 0 && (
          <details className="mt-2">
            <summary className="opacity-70 hover:opacity-100 cursor-pointer text-[11px] uppercase tracking-wider">
              View all reservations ({status.rows.length})
            </summary>
            <div className="mt-2 max-h-[400px] overflow-y-auto">
              <table className="w-full text-[11px] tabular-nums">
                <thead className="bg-white/[0.04] sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1 border-b border-white/15 font-normal uppercase tracking-wider text-[10px] opacity-65">Name / email</th>
                    <th className="text-left px-2 py-1 border-b border-white/15 font-normal uppercase tracking-wider text-[10px] opacity-65">Reserved</th>
                    <th className="text-left px-2 py-1 border-b border-white/15 font-normal uppercase tracking-wider text-[10px] opacity-65">Status</th>
                    <th className="text-left px-2 py-1 border-b border-white/15 font-normal uppercase tracking-wider text-[10px] opacity-65">Link</th>
                  </tr>
                </thead>
                <tbody>
                  {status.rows.map((r) => {
                    const link = r.fulfillment_token ? `${baseUrl}/vinyl/fulfill/${r.fulfillment_token}` : null;
                    const statusLabel = r.fulfilled_at
                      ? `Paid · ${r.fulfilled_quantity || "?"}×`
                      : r.fulfillment_sent_at ? "Sent" : "Outstanding";
                    const statusColor = r.fulfilled_at
                      ? "text-emerald-300"
                      : r.fulfillment_sent_at ? "text-[var(--mm-accent)]" : "opacity-65";
                    return (
                      <tr key={r.id} className="border-b border-white/5 last:border-b-0 hover:bg-white/[0.025]">
                        <td className="px-2 py-1 align-top">
                          <div>{r.name || "—"}</div>
                          <div className="opacity-55 text-[10px]">{r.email}</div>
                        </td>
                        <td className="px-2 py-1 align-top">{r.reserved_quantity}</td>
                        <td className={`px-2 py-1 align-top ${statusColor}`}>{statusLabel}</td>
                        <td className="px-2 py-1 align-top">
                          {link ? (
                            <CopyButton value={link} testid={`copy-vinyl-fulfill-${r.id}`} />
                          ) : (
                            <span className="opacity-40">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </div>
    </Section>
  );
}


/* ─── Vinyl orders to ship ─────────────────────────────────────────── */
function VinylOrdersToShip({ adminKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [savingId, setSavingId] = useState(null);
  const [trackingDraft, setTrackingDraft] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get("/admin/vinyl-orders", {
        headers: { "X-Admin-Key": adminKey },
      });
      setData(r);
    } catch {
      setData({ error: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!adminKey) return undefined;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data: r } = await api.get("/admin/vinyl-orders", {
          headers: { "X-Admin-Key": adminKey },
        });
        if (alive) setData(r);
      } catch {
        if (alive) setData({ error: true });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [adminKey]);

  const onMarkShipped = async (row, shipped) => {
    setSavingId(row.session_id);
    try {
      await api.post(
        "/admin/vinyl-orders/mark-shipped",
        {
          session_id: row.session_id,
          shipped,
          tracking_number: trackingDraft[row.session_id] || row.tracking_number || null,
        },
        { headers: { "X-Admin-Key": adminKey } },
      );
      await load();
    } finally {
      setSavingId(null);
    }
  };

  const rows = (data?.rows || []).filter((r) => {
    if (filter === "unshipped") return !r.shipped_at;
    if (filter === "shipped") return !!r.shipped_at;
    return true;
  });

  return (
    <Section
      title="Vinyl orders to ship"
      right={
        <button
          onClick={load}
          className="text-[10px] uppercase tracking-wider opacity-70 hover:opacity-100"
          data-testid="vinyl-orders-refresh"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      }
    >
      <div className="border border-white/15 px-4 py-4 space-y-3 text-[12px]">
        <p className="opacity-80 leading-relaxed">
          Every paid vinyl order with its captured shipping address. Use the
          suggested depot column to fulfil from Australia or the UK. Tick rows
          shipped (with optional tracking) as they leave.
        </p>

        {data && !data.error && (
          <div className="border border-white/10 px-3 py-2 bg-white/[0.025] flex flex-wrap items-center gap-3">
            <span className="opacity-80">
              Paid orders: <b>{data.count}</b> ·
              Records out: <b className="text-[var(--mm-accent)]">{data.total_records}</b> ·
              Gross: <b>${data.total_revenue?.toFixed(2)} AUD</b>
            </span>
            <span className="opacity-70">
              Depot split — AU: <b>{data.depot_split?.AU || 0}</b> · UK: <b>{data.depot_split?.UK || 0}</b>
            </span>
          </div>
        )}

        <div className="flex gap-2">
          {[["all", "All"], ["unshipped", "To ship"], ["shipped", "Shipped"]].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`px-2.5 py-1 uppercase tracking-wider text-[10px] border ${filter === k ? "border-[var(--mm-accent)] text-[var(--mm-accent)]" : "border-white/20 opacity-60 hover:opacity-100"}`}
              data-testid={`vinyl-orders-filter-${k}`}
            >
              {label}
            </button>
          ))}
        </div>

        {rows.length === 0 ? (
          <p className="opacity-60 text-[11px] py-4">No orders match this filter yet.</p>
        ) : (
          <div className="max-h-[560px] overflow-y-auto">
            <table className="w-full text-[11px] tabular-nums">
              <thead className="bg-white/[0.04] sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1 border-b border-white/15 font-normal uppercase tracking-wider text-[10px] opacity-65">Buyer</th>
                  <th className="text-left px-2 py-1 border-b border-white/15 font-normal uppercase tracking-wider text-[10px] opacity-65">Qty</th>
                  <th className="text-left px-2 py-1 border-b border-white/15 font-normal uppercase tracking-wider text-[10px] opacity-65">Ship to</th>
                  <th className="text-left px-2 py-1 border-b border-white/15 font-normal uppercase tracking-wider text-[10px] opacity-65">Depot</th>
                  <th className="text-left px-2 py-1 border-b border-white/15 font-normal uppercase tracking-wider text-[10px] opacity-65">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const addr = [r.address_line1, r.address_line2,
                    [r.city, r.state, r.postal_code].filter(Boolean).join(" "),
                    r.country].filter(Boolean).join(", ");
                  return (
                    <tr key={r.session_id} className="border-b border-white/5 last:border-b-0 hover:bg-white/[0.025] align-top">
                      <td className="px-2 py-2">
                        <div>{r.buyer_name || "—"}</div>
                        <div className="opacity-55 text-[10px]">{r.email}</div>
                        {r.ship_to_phone && <div className="opacity-55 text-[10px]">{r.ship_to_phone}</div>}
                        <div className="opacity-40 text-[10px] mt-0.5">
                          {r.paid_at ? new Date(r.paid_at).toLocaleDateString() : ""}
                          {" · "}${Number(r.amount || 0).toFixed(2)} {r.currency}
                        </div>
                      </td>
                      <td className="px-2 py-2">{r.quantity}</td>
                      <td className="px-2 py-2 leading-snug">
                        <div className="text-white/90 text-[11px]">{r.ship_to_name}</div>
                        <div className="text-white/70 text-[11px]">{addr || <span className="opacity-40">Pending</span>}</div>
                      </td>
                      <td className="px-2 py-2">
                        <span className={`uppercase text-[10px] tracking-wider px-1.5 py-0.5 border ${r.suggested_depot === "AU" ? "border-emerald-400/40 text-emerald-200" : "border-amber-400/40 text-amber-200"}`}>
                          {r.suggested_depot}
                        </span>
                      </td>
                      <td className="px-2 py-2 space-y-1.5">
                        {r.shipped_at ? (
                          <>
                            <div className="text-emerald-300 text-[10px] uppercase tracking-wider">Shipped</div>
                            {r.tracking_number && <div className="opacity-70 text-[10px]">{r.tracking_number}</div>}
                            <button
                              onClick={() => onMarkShipped(r, false)}
                              disabled={savingId === r.session_id}
                              className="opacity-50 hover:opacity-100 text-[10px] underline"
                              data-testid={`vinyl-unship-${r.session_id}`}
                            >
                              Undo
                            </button>
                          </>
                        ) : (
                          <>
                            <input
                              type="text"
                              placeholder="Tracking #"
                              value={trackingDraft[r.session_id] || ""}
                              onChange={(e) => setTrackingDraft({ ...trackingDraft, [r.session_id]: e.target.value })}
                              className="w-28 bg-black border border-white/20 focus:border-[var(--mm-accent)] outline-none px-1.5 py-0.5 text-[10px]"
                              data-testid={`vinyl-tracking-${r.session_id}`}
                            />
                            <button
                              onClick={() => onMarkShipped(r, true)}
                              disabled={savingId === r.session_id}
                              className="block bg-[var(--mm-accent)] text-black px-2 py-0.5 uppercase tracking-wider text-[10px] disabled:opacity-30"
                              data-testid={`vinyl-ship-${r.session_id}`}
                            >
                              {savingId === r.session_id ? "Saving…" : "Mark shipped"}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Section>
  );
}




/* ─── Newsletter signups browser — grouped by city + interest ────── */
function NewsletterSignupsBrowser({ adminKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cityFilter, setCityFilter] = useState(null);   // null = all
  const [interestFilter, setInterestFilter] = useState(null);

  useEffect(() => {
    if (!adminKey) return undefined;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data: r } = await api.get("/admin/interest/newsletter", {
          headers: { "X-Admin-Key": adminKey },
        });
        if (alive) setData(r);
      } catch {
        if (alive) setData({ error: true });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [adminKey]);

  const items = data?.items || [];
  const optionMap = Object.fromEntries((data?.options || []).map((o) => [o.key, o.label]));

  // City breakdown (case-insensitive bucketing on the trimmed value)
  const cityCounts = items.reduce((acc, r) => {
    const k = (r.city || "—").trim();
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const cities = Object.entries(cityCounts).sort((a, b) => b[1] - a[1]);

  // Interest breakdown — each signup can have multiple categories
  const interestCounts = items.reduce((acc, r) => {
    for (const k of (r.interests || [])) acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const interests = Object.entries(interestCounts).sort((a, b) => b[1] - a[1]);

  // Apply filters
  const filtered = items.filter((r) => {
    if (cityFilter && (r.city || "—").trim() !== cityFilter) return false;
    if (interestFilter && !(r.interests || []).includes(interestFilter)) return false;
    return true;
  });

  return (
    <Section title={`Newsletter signups · ${items.length}`}>
      <div className="border border-white/15 px-4 py-4 space-y-4 text-[12px]">
        {loading && <p className="opacity-60 text-[11px]">Loading…</p>}
        {data?.error && <p className="text-rose-300/80 text-[11px]">Could not load signups</p>}

        {/* CITY pills */}
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] opacity-65 mb-2">By location</p>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setCityFilter(null)}
              className={`px-2 py-0.5 uppercase tracking-wider text-[10px] border ${
                cityFilter === null ? "border-[var(--mm-accent)] text-[var(--mm-accent)]" : "border-white/20 opacity-60 hover:opacity-100"
              }`}
              data-testid="newsletter-city-all"
            >
              All · {items.length}
            </button>
            {cities.map(([c, n]) => (
              <button
                key={c}
                onClick={() => setCityFilter(c === cityFilter ? null : c)}
                className={`px-2 py-0.5 text-[11px] border ${
                  cityFilter === c ? "border-[var(--mm-accent)] text-[var(--mm-accent)]" : "border-white/20 opacity-70 hover:opacity-100"
                }`}
                data-testid={`newsletter-city-${c}`}
              >
                {c} <span className="opacity-50">· {n}</span>
              </button>
            ))}
          </div>
        </div>

        {/* INTEREST pills */}
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] opacity-65 mb-2">By interest</p>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setInterestFilter(null)}
              className={`px-2 py-0.5 uppercase tracking-wider text-[10px] border ${
                interestFilter === null ? "border-[var(--mm-accent)] text-[var(--mm-accent)]" : "border-white/20 opacity-60 hover:opacity-100"
              }`}
              data-testid="newsletter-interest-all"
            >
              All
            </button>
            {interests.map(([k, n]) => (
              <button
                key={k}
                onClick={() => setInterestFilter(k === interestFilter ? null : k)}
                className={`px-2 py-0.5 text-[11px] border ${
                  interestFilter === k ? "border-[var(--mm-accent)] text-[var(--mm-accent)]" : "border-white/20 opacity-70 hover:opacity-100"
                }`}
                data-testid={`newsletter-interest-pill-${k}`}
              >
                {optionMap[k] || k} <span className="opacity-50">· {n}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Active filter chip + count */}
        {(cityFilter || interestFilter) && (
          <div className="text-[11px] opacity-75 flex items-center gap-2 flex-wrap">
            Showing <b className="text-[var(--mm-accent)]">{filtered.length}</b> of {items.length}
            {cityFilter && <span className="border border-white/25 px-1.5 py-0.5">{cityFilter}</span>}
            {interestFilter && <span className="border border-white/25 px-1.5 py-0.5">{optionMap[interestFilter] || interestFilter}</span>}
            <button
              onClick={() => { setCityFilter(null); setInterestFilter(null); }}
              className="underline opacity-60 hover:opacity-100"
              data-testid="newsletter-clear-filters"
            >
              clear
            </button>
          </div>
        )}

        {filtered.length === 0 ? (
          <p className="opacity-60 text-[11px] py-3">No signups match these filters yet.</p>
        ) : (
          <div className="max-h-[440px] overflow-y-auto">
            <table className="w-full text-[11px]">
              <thead className="bg-white/[0.04] sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1 border-b border-white/15 font-normal uppercase tracking-wider text-[10px] opacity-65">Date</th>
                  <th className="text-left px-2 py-1 border-b border-white/15 font-normal uppercase tracking-wider text-[10px] opacity-65">Name</th>
                  <th className="text-left px-2 py-1 border-b border-white/15 font-normal uppercase tracking-wider text-[10px] opacity-65">Email</th>
                  <th className="text-left px-2 py-1 border-b border-white/15 font-normal uppercase tracking-wider text-[10px] opacity-65">City</th>
                  <th className="text-left px-2 py-1 border-b border-white/15 font-normal uppercase tracking-wider text-[10px] opacity-65">Interests</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={r.id || i} className="border-b border-white/5 last:border-b-0 hover:bg-white/[0.025] align-top">
                    <td className="px-2 py-1.5 opacity-65 text-[10px] whitespace-nowrap">
                      {r.received_at ? new Date(r.received_at).toLocaleDateString() : ""}
                    </td>
                    <td className="px-2 py-1.5">{r.name || "—"}</td>
                    <td className="px-2 py-1.5 opacity-80">{r.email}</td>
                    <td className="px-2 py-1.5">{r.city || "—"}</td>
                    <td className="px-2 py-1.5">
                      <div className="flex flex-wrap gap-1">
                        {(r.interests || []).map((k) => (
                          <span
                            key={k}
                            className="border border-[var(--mm-accent)]/40 text-[var(--mm-accent)] px-1.5 py-0 text-[9px] uppercase tracking-wider"
                          >
                            {optionMap[k] || k}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Section>
  );
}


/* ─── Share links + presale logging ──────────────────────────────────── */
function ShareLinksPanel({ adminKey }) {
  const [presaleEmails, setPresaleEmails] = useState("");
  const [presaleAmount, setPresaleAmount] = useState(45);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  // Always show production links so they're copy-paste-ready regardless of
  // which environment the admin is logged into.
  const base = "https://metamorph-george-maple.com";

  const groups = [
    {
      heading: "Discovery",
      links: [
        { label: "All shows landing",    url: `${base}/tickets` },
        { label: "Sydney show page",     url: `${base}/shows/sydney` },
        { label: "Adelaide show page",   url: `${base}/shows/adelaide` },
        { label: "London show page",     url: `${base}/shows/london` },
      ],
    },
    {
      heading: "Direct ticket purchase",
      links: [
        { label: "Sydney · regular",     url: `${base}/buy/sydney` },
        { label: "Adelaide · regular",   url: `${base}/buy/adelaide` },
        { label: "London · regular",     url: `${base}/buy/london` },
      ],
    },
    {
      heading: "Artist ticket (friends & family · afterparty + champagne)",
      links: [
        { label: "Sydney · artist",      url: `${base}/buy/sydney-artist` },
        { label: "Adelaide · artist",    url: `${base}/buy/adelaide-artist` },
        { label: "London · artist",      url: `${base}/buy/london-artist` },
      ],
    },
    {
      heading: "Other",
      links: [
        { label: "Vinyl pre-order home", url: `${base}/` },
        { label: "Manifesto",            url: `${base}/manifesto` },
        { label: "Admin dashboard",      url: `${base}/admin#k=${adminKey || "..."}` },
        { label: "Door scanner",         url: `${base}/admin/scan` },
      ],
    },
    {
      heading: "Coming-soon previews (hidden from /tickets until announced)",
      links: [
        { label: "Melbourne preview",    url: `${base}/shows/melbourne` },
        { label: "Paris preview",        url: `${base}/shows/paris` },
        { label: "Perth preview",        url: `${base}/shows/perth` },
        { label: "Gold Coast preview",   url: `${base}/shows/gold_coast` },
      ],
    },
  ];

  const onSeedPresales = async () => {
    setSeeding(true);
    setSeedResult(null);
    const emails = presaleEmails
      .split(/[\s,;\n]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    try {
      const { data } = await api.post(
        "/admin/seed-london-presales",
        { emails, amount: Number(presaleAmount) || 45 },
        { headers: { "X-Admin-Key": adminKey } },
      );
      setSeedResult(data);
    } catch (e) {
      setSeedResult({ error: e?.response?.data?.detail || "Could not seed presales" });
    } finally {
      setSeeding(false);
    }
  };

  return (
    <Section title="Share links + presale logging">
      <div className="border border-white/15 px-4 py-4 space-y-5 text-[12px]">
        <p className="opacity-80 leading-relaxed">
          Tap a link to copy. Share these directly with anyone — they bypass
          the admin and the SiteGate. Production URLs are shown regardless of
          which environment you&rsquo;re logged into.
        </p>

        {groups.map((g) => (
          <div key={g.heading}>
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] opacity-65 mb-2">{g.heading}</p>
            <div className="space-y-1.5">
              {g.links.map((l) => (
                <div key={l.url} className="flex items-center justify-between gap-3 border border-white/10 px-2.5 py-1.5 hover:border-white/30 transition">
                  <span className="text-[11px] opacity-80 truncate" title={l.url}>
                    <b className="opacity-100">{l.label}</b>
                    <span className="opacity-50 ml-2">{l.url}</span>
                  </span>
                  <CopyButton value={l.url} testid={`share-copy-${l.url}`} />
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* ── Seed the London presales (one-click curl replacement) ── */}
        <div className="pt-4 border-t border-white/15">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] opacity-65 mb-2">
            Seed London pre-sales (already paid outside Stripe)
          </p>
          <p className="opacity-65 leading-relaxed mb-3 text-[11px]">
            Paste up to 5 buyer emails (one per line, or comma-separated) and click
            <b className="text-[var(--mm-accent)] mx-1">Seed</b>.
            Idempotent — re-running with the same email skips it. Leave blank to
            create 5 anonymous placeholder rows.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 mb-2">
            <textarea
              value={presaleEmails}
              onChange={(e) => setPresaleEmails(e.target.value)}
              placeholder={"buyer1@example.com\nbuyer2@example.com\n…"}
              rows={4}
              className="flex-1 bg-black border border-white/20 focus:border-[var(--mm-accent)] outline-none px-2 py-1.5 text-[12px]"
              data-testid="presale-emails"
            />
            <div className="flex sm:flex-col gap-2 sm:w-32">
              <label className="flex items-center gap-1 text-[10px] uppercase tracking-wider opacity-70">
                £
                <input
                  type="number"
                  min="1"
                  value={presaleAmount}
                  onChange={(e) => setPresaleAmount(e.target.value)}
                  className="w-16 bg-black border border-white/20 focus:border-[var(--mm-accent)] outline-none px-2 py-1 text-[12px]"
                  data-testid="presale-amount"
                />
              </label>
              <button
                onClick={onSeedPresales}
                disabled={seeding}
                className="bg-[var(--mm-accent)] text-black px-3 py-1.5 uppercase tracking-wider text-[10px] disabled:opacity-30"
                data-testid="presale-seed"
              >
                {seeding ? "Seeding…" : "Seed"}
              </button>
            </div>
          </div>
          {seedResult && !seedResult.error && (
            <p className="border-l-2 border-emerald-400/60 pl-3 py-1 text-emerald-200 text-[11px]" data-testid="presale-result">
              Inserted <b>{seedResult.inserted}</b>. Skipped (already logged): <b>{(seedResult.skipped || []).length}</b>.
              {(seedResult.tickets || []).length > 0 && (
                <span className="block opacity-75 mt-1">
                  References: {(seedResult.tickets || []).map((t) => t.reference).join(", ")}
                </span>
              )}
            </p>
          )}
          {seedResult?.error && (
            <p className="border-l-2 border-rose-400/60 pl-3 py-1 text-rose-200 text-[11px]">{seedResult.error}</p>
          )}
        </div>
      </div>
    </Section>
  );
}

